import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip as ChartTooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import {
  DEFAULT_FLEET_OPTIMISATION_VALUES,
  FleetOptimisationNumericInputKey,
  FleetOptimisationFormValues,
  FleetOptimisationResult,
  FleetOptimisationValidationErrors,
  calculateFleetOptimisation,
  parseFleetOptimisationInputs,
} from '../services/fleetOptimisationCalculator';
import { trackAnalyticsEvent } from '../services/analytics';
import { ANALYTICS_EVENT_NAMES } from '../services/eventTaxonomy';
import type {
  FleetOptimiserCapexSection,
  FleetOptimiserFieldGroup,
  FleetOptimiserSavingsBucket,
  ToolLaunchSource,
} from '../services/eventTypes';

import { Icon } from '@liftango/ui-icons';
const FIELD_TO_GROUP: Record<FleetOptimisationNumericInputKey, FleetOptimiserFieldGroup> = {
  currentFleet: 'fleet_size',
  optimizedFleet: 'fleet_size',
  baselineDailyKm: 'distance',
  optimizedDailyKm: 'distance',
  costPerKm: 'cost',
  annualFixedCost: 'cost',
  baselineCo2GramsPerKm: 'co2',
  optimizedCo2GramsPerKm: 'co2',
  analysisYears: 'horizon',
  baselineCapexPerVehicle: 'capex_baseline',
  baselineVehiclesWithCapex: 'capex_baseline',
  optimizedCapexPerVehicle: 'capex_optimized',
  optimizedVehiclesWithCapex: 'capex_optimized',
  baselineDriverHoursPerVehicle: 'cost',
  optimizedDriverHoursPerVehicle: 'cost',
  driverCostPerHour: 'cost',
};

function bucketSavings(percentage: number | null): FleetOptimiserSavingsBucket {
  if (percentage === null || !Number.isFinite(percentage) || percentage < 0) {
    return 'negative';
  }
  if (percentage < 5) {
    return 'small';
  }
  if (percentage < 20) {
    return 'medium';
  }
  return 'large';
}

function deriveLaunchSource(referrer: string | null, search: string): ToolLaunchSource {
  const params = new URLSearchParams(search);
  if (params.has('shared')) {
    return 'shared_link';
  }
  if (referrer && referrer.includes('/tools')) {
    return 'directory';
  }
  return 'direct_url';
}

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip, Legend);

const GBP_FORMATTER = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const PERCENT_FORMATTER = new Intl.NumberFormat('en-GB', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const CO2_LABEL = (
  <>
    CO<sub>2</sub>
  </>
);

type FieldDefinition = {
  key: Exclude<
    keyof FleetOptimisationFormValues,
    | 'baselineCapexEnabled'
    | 'optimizedCapexEnabled'
    | 'baselineCapexPerVehicle'
    | 'baselineVehiclesWithCapex'
    | 'optimizedCapexPerVehicle'
    | 'optimizedVehiclesWithCapex'
    | 'analysisYears'
    | 'baselineDriverHoursEnabled'
    | 'optimizedDriverHoursEnabled'
    | 'baselineDriverHoursPerVehicle'
    | 'optimizedDriverHoursPerVehicle'
    | 'driverCostPerHour'
  >;
  label: React.ReactNode;
  helperText: React.ReactNode;
  step?: string;
};

type CapexFieldDefinition = {
  key: 'baselineCapexPerVehicle' | 'baselineVehiclesWithCapex' | 'optimizedCapexPerVehicle' | 'optimizedVehiclesWithCapex';
  label: string;
  helperText: string;
  step?: string;
  min?: number;
};

type CapexSection = {
  title: string;
  toggleKey: 'baselineCapexEnabled' | 'optimizedCapexEnabled';
  toggleLabel: string;
  toggleAriaLabel: string;
  description: string;
  fieldKeys: Array<'baselineCapexPerVehicle' | 'baselineVehiclesWithCapex' | 'optimizedCapexPerVehicle' | 'optimizedVehiclesWithCapex'>;
};

const FleetOptimisationPage: React.FC = () => {
  const { t } = useTranslation('simulation');
  const location = useLocation();

  const fieldDefinitions: FieldDefinition[] = [
    { key: 'currentFleet', label: t('fleetOptimisation.inputs.currentFleet.label'), helperText: t('fleetOptimisation.inputs.currentFleet.helper'), step: '1' },
    { key: 'optimizedFleet', label: t('fleetOptimisation.inputs.optimizedFleet.label'), helperText: t('fleetOptimisation.inputs.optimizedFleet.helper'), step: '1' },
    { key: 'baselineDailyKm', label: t('fleetOptimisation.inputs.baselineDailyKm.label'), helperText: t('fleetOptimisation.inputs.baselineDailyKm.helper'), step: '0.1' },
    { key: 'optimizedDailyKm', label: t('fleetOptimisation.inputs.optimizedDailyKm.label'), helperText: t('fleetOptimisation.inputs.optimizedDailyKm.helper'), step: '0.1' },
    { key: 'costPerKm', label: t('fleetOptimisation.inputs.costPerKm.label'), helperText: t('fleetOptimisation.inputs.costPerKm.helper'), step: '0.01' },
    { key: 'annualFixedCost', label: t('fleetOptimisation.inputs.annualFixedCost.label'), helperText: t('fleetOptimisation.inputs.annualFixedCost.helper'), step: '0.01' },
    {
      key: 'baselineCo2GramsPerKm',
      label: (<>{t('fleetOptimisation.inputs.baselineCo2.labelPrefix')}{CO2_LABEL}{t('fleetOptimisation.inputs.baselineCo2.labelSuffix')}</>),
      helperText: (<>{t('fleetOptimisation.inputs.baselineCo2.helperPrefix')}{CO2_LABEL}{t('fleetOptimisation.inputs.baselineCo2.helperSuffix')}</>),
      step: '1',
    },
    {
      key: 'optimizedCo2GramsPerKm',
      label: (<>{t('fleetOptimisation.inputs.optimizedCo2.labelPrefix')}{CO2_LABEL}{t('fleetOptimisation.inputs.optimizedCo2.labelSuffix')}</>),
      helperText: (<>{t('fleetOptimisation.inputs.optimizedCo2.helperPrefix')}{CO2_LABEL}{t('fleetOptimisation.inputs.optimizedCo2.helperSuffix')}</>),
      step: '1',
    },
  ];

  const capexFieldDefinitions: CapexFieldDefinition[] = [
    { key: 'baselineCapexPerVehicle', label: t('fleetOptimisation.inputs.baselineCapexPerVehicle.label'), helperText: t('fleetOptimisation.inputs.baselineCapexPerVehicle.helper'), step: '0.01', min: 0 },
    { key: 'baselineVehiclesWithCapex', label: t('fleetOptimisation.inputs.baselineVehiclesWithCapex.label'), helperText: t('fleetOptimisation.inputs.baselineVehiclesWithCapex.helper'), step: '1', min: 0 },
    { key: 'optimizedCapexPerVehicle', label: t('fleetOptimisation.inputs.optimizedCapexPerVehicle.label'), helperText: t('fleetOptimisation.inputs.optimizedCapexPerVehicle.helper'), step: '0.01', min: 0 },
    { key: 'optimizedVehiclesWithCapex', label: t('fleetOptimisation.inputs.optimizedVehiclesWithCapex.label'), helperText: t('fleetOptimisation.inputs.optimizedVehiclesWithCapex.helper'), step: '1', min: 0 },
  ];

  const analysisFieldDefinition: {
    key: 'analysisYears';
    label: string;
    helperText: string;
    step: string;
    min: number;
  } = {
    key: 'analysisYears',
    label: t('fleetOptimisation.inputs.analysisYears.label'),
    helperText: t('fleetOptimisation.inputs.analysisYears.helper'),
    step: '1',
    min: 1,
  };

  const capexSections: CapexSection[] = [
    {
      title: t('fleetOptimisation.capex.baselineTitle'),
      toggleKey: 'baselineCapexEnabled',
      toggleLabel: t('fleetOptimisation.capex.baselineToggle'),
      toggleAriaLabel: t('fleetOptimisation.capex.baselineToggle'),
      description: t('fleetOptimisation.capex.baselineDescription'),
      fieldKeys: ['baselineCapexPerVehicle', 'baselineVehiclesWithCapex'],
    },
    {
      title: t('fleetOptimisation.capex.optimizedTitle'),
      toggleKey: 'optimizedCapexEnabled',
      toggleLabel: t('fleetOptimisation.capex.optimizedToggle'),
      toggleAriaLabel: t('fleetOptimisation.capex.optimizedToggle'),
      description: t('fleetOptimisation.capex.optimizedDescription'),
      fieldKeys: ['optimizedCapexPerVehicle', 'optimizedVehiclesWithCapex'],
    },
  ];

  const [formValues, setFormValues] = useState<FleetOptimisationFormValues>(DEFAULT_FLEET_OPTIMISATION_VALUES);
  const [errors, setErrors] = useState<FleetOptimisationValidationErrors>({});
  const [results, setResults] = useState<FleetOptimisationResult>(() => {
    const parsed = parseFleetOptimisationInputs(DEFAULT_FLEET_OPTIMISATION_VALUES);
    return calculateFleetOptimisation(parsed.inputs!);
  });
  const [hasValidationError, setHasValidationError] = useState<boolean>(false);
  const trackedFieldGroupsRef = useRef<Set<FleetOptimiserFieldGroup>>(new Set());

  useEffect(() => {
    const referrer = typeof document !== 'undefined' ? document.referrer : null;
    const source = deriveLaunchSource(referrer, location.search);
    trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.tool_launched, {
      tool_id: 'fleet_optimisation',
      source,
      timestamp: new Date().toISOString(),
    });
    // Mount-only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const trackFieldGroupOnce = (fieldKey: FleetOptimisationNumericInputKey, value: string): void => {
    if (value === DEFAULT_FLEET_OPTIMISATION_VALUES[fieldKey]) {
      return;
    }
    const group = FIELD_TO_GROUP[fieldKey];
    if (!group || trackedFieldGroupsRef.current.has(group)) {
      return;
    }
    trackedFieldGroupsRef.current.add(group);
    trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.fleet_optimiser_field_group_changed, {
      field_group: group,
      timestamp: new Date().toISOString(),
    });
  };

  const handleChange = (
    fieldKey: FleetOptimisationNumericInputKey,
    value: string
  ): void => {
    trackFieldGroupOnce(fieldKey, value);
    setFormValues((previous) => {
      const nextValues = { ...previous, [fieldKey]: value };

      if (fieldKey === 'currentFleet') {
        const currentFleet = Number(value);
        const baselineVehiclesWithCapex = Number(previous.baselineVehiclesWithCapex);

        if (Number.isFinite(currentFleet) && Number.isFinite(baselineVehiclesWithCapex) && baselineVehiclesWithCapex > currentFleet) {
          nextValues.baselineVehiclesWithCapex = String(Math.max(0, currentFleet));
        }
      }

      if (fieldKey === 'optimizedFleet') {
        const optimizedFleet = Number(value);
        const optimizedVehiclesWithCapex = Number(previous.optimizedVehiclesWithCapex);

        if (Number.isFinite(optimizedFleet) && Number.isFinite(optimizedVehiclesWithCapex) && optimizedVehiclesWithCapex > optimizedFleet) {
          nextValues.optimizedVehiclesWithCapex = String(Math.max(0, optimizedFleet));
        }
      }

      return nextValues;
    });

    setErrors((previous) => {
      if (!previous[fieldKey]) {
        return previous;
      }

      const next = { ...previous };
      delete next[fieldKey];
      return next;
    });
  };

  const handleCapexToggle = (toggleKey: 'baselineCapexEnabled' | 'optimizedCapexEnabled', enabled: boolean): void => {
    const section: FleetOptimiserCapexSection = toggleKey === 'baselineCapexEnabled' ? 'baseline' : 'optimized';
    trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.fleet_optimiser_capex_toggled, {
      section,
      enabled,
      timestamp: new Date().toISOString(),
    });
    setFormValues((previous) => ({ ...previous, [toggleKey]: enabled }));
    setErrors((previous) => {
      const relatedFields = toggleKey === 'baselineCapexEnabled'
        ? (['baselineCapexPerVehicle', 'baselineVehiclesWithCapex'] as const)
        : (['optimizedCapexPerVehicle', 'optimizedVehiclesWithCapex'] as const);

      if (!relatedFields.some((field) => previous[field])) {
        return previous;
      }

      const next = { ...previous };
      relatedFields.forEach((field) => {
        delete next[field];
      });
      return next;
    });
  };

  const handleAnalyze = (): void => {
    const parsed = parseFleetOptimisationInputs(formValues);
    const hasErrors = !parsed.isValid || !parsed.inputs;
    trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.fleet_optimiser_analyze_clicked, {
      has_validation_errors: hasErrors,
      timestamp: new Date().toISOString(),
    });
    if (hasErrors || !parsed.inputs) {
      setErrors(parsed.errors);
      setHasValidationError(true);
      return;
    }

    const computed = calculateFleetOptimisation(parsed.inputs);
    setResults(computed);
    setErrors({});
    setHasValidationError(false);

    const analysisYears = Number(formValues.analysisYears) || 0;
    const hasCapex = Boolean(formValues.baselineCapexEnabled) || Boolean(formValues.optimizedCapexEnabled);
    const savingsPercentage = computed.baselineCost > 0
      ? (computed.financialSavings / computed.baselineCost) * 100
      : null;
    trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.fleet_optimiser_result_displayed, {
      analysis_years: analysisYears,
      has_capex: hasCapex,
      savings_bucket: bucketSavings(savingsPercentage),
      timestamp: new Date().toISOString(),
    });
  };

  const annualFinancialLabel = results.financialSavings >= 0 ? t('fleetOptimisation.annualSavings') : t('fleetOptimisation.annualImpact');
  const totalFinancialLabel = results.totalFinancialSavings >= 0 ? t('fleetOptimisation.totalSavings') : t('fleetOptimisation.totalImpact');
  const annualCo2Label = results.co2ReductionTonnes >= 0
    ? t('fleetOptimisation.annualCo2Reduction')
    : t('fleetOptimisation.annualCo2Impact');
  const totalCo2Label = results.totalCo2ReductionTonnes >= 0
    ? t('fleetOptimisation.totalCo2Reduction')
    : t('fleetOptimisation.totalCo2Impact');

  const costChartData = useMemo(() => ({
    labels: [t('fleetOptimisation.charts.annual'), t('fleetOptimisation.charts.total')],
    datasets: [
      {
        label: t('fleetOptimisation.charts.currentCost'),
        data: [results.baselineCost, results.totalBaselineCost],
        backgroundColor: '#4b5563',
        borderRadius: 8,
      },
      {
        label: t('fleetOptimisation.charts.optimizedCost'),
        data: [results.optimizedCost, results.totalOptimizedCost],
        backgroundColor: '#166534',
        borderRadius: 8,
      },
    ],
  }), [results.baselineCost, results.optimizedCost, results.totalBaselineCost, results.totalOptimizedCost, t]);

  const co2ChartData = useMemo(() => ({
    labels: [t('fleetOptimisation.charts.annual'), t('fleetOptimisation.charts.total')],
    datasets: [
      {
        label: t('fleetOptimisation.charts.currentCo2'),
        data: [results.baselineCo2Tonnes, results.totalBaselineCo2Tonnes],
        backgroundColor: '#0e7490',
        borderRadius: 8,
      },
      {
        label: t('fleetOptimisation.charts.optimizedCo2'),
        data: [results.optimizedCo2Tonnes, results.totalOptimizedCo2Tonnes],
        backgroundColor: '#0f766e',
        borderRadius: 8,
      },
    ],
  }), [results.baselineCo2Tonnes, results.optimizedCo2Tonnes, results.totalBaselineCo2Tonnes, results.totalOptimizedCo2Tonnes, t]);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={1} sx={{ p: { xs: 2, md: 4 } }}>
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            p: { xs: 2, md: 3 },
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            background: 'linear-gradient(135deg, rgba(3, 105, 161, 0.06), rgba(22, 163, 74, 0.06))',
          }}
        >
          <Typography variant="h4" component="h1" gutterBottom>
            {t('fleetOptimisation.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('fleetOptimisation.subtitle')}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 2 }}>
            <Chip label={t('fleetOptimisation.chips.analysisYears', { value: formValues.analysisYears || '-' })} size="small" color="primary" variant="outlined" />
            <Chip
              label={t('fleetOptimisation.chips.baselineCapex', { state: formValues.baselineCapexEnabled ? t('fleetOptimisation.chips.on') : t('fleetOptimisation.chips.off') })}
              size="small"
              color={formValues.baselineCapexEnabled ? 'success' : 'default'}
              variant="outlined"
            />
            <Chip
              label={t('fleetOptimisation.chips.optimizedCapex', { state: formValues.optimizedCapexEnabled ? t('fleetOptimisation.chips.on') : t('fleetOptimisation.chips.off') })}
              size="small"
              color={formValues.optimizedCapexEnabled ? 'success' : 'default'}
              variant="outlined"
            />
          </Stack>
        </Paper>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          {fieldDefinitions.map((field) => (
            <Grid size={{ xs: 12, sm: 6 }} key={field.key}>
              <TextField
                label={field.label}
                value={formValues[field.key]}
                onChange={(event) => handleChange(field.key, event.target.value)}
                error={Boolean(errors[field.key])}
                helperText={errors[field.key] || field.helperText}
                fullWidth
                type="number"
                inputProps={{ step: field.step, min: 0 }}
              />
            </Grid>
          ))}
        </Grid>

        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: 'divider' }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            {t('fleetOptimisation.analysisSettings')}
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label={analysisFieldDefinition.label}
                value={formValues[analysisFieldDefinition.key]}
                onChange={(event) => handleChange(analysisFieldDefinition.key, event.target.value)}
                error={Boolean(errors[analysisFieldDefinition.key])}
                helperText={errors[analysisFieldDefinition.key] || analysisFieldDefinition.helperText}
                fullWidth
                type="number"
                inputProps={{ step: analysisFieldDefinition.step, min: analysisFieldDefinition.min }}
              />
            </Grid>
          </Grid>
        </Paper>

        {capexSections.map((section) => (
          <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: 'divider' }} key={section.title}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              {section.title}
            </Typography>
            <FormControlLabel
              control={(
                <Switch
                  checked={formValues[section.toggleKey]}
                  onChange={(_, checked) => handleCapexToggle(section.toggleKey, checked)}
                  inputProps={{ 'aria-label': section.toggleAriaLabel }}
                />
              )}
              label={section.toggleLabel}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {section.description}
            </Typography>
            <Grid container spacing={2}>
              {section.fieldKeys.map((fieldKey) => {
                const field = capexFieldDefinitions.find((definition) => definition.key === fieldKey);
                if (!field) {
                  return null;
                }

                return (
                  <Grid size={{ xs: 12, sm: 6 }} key={field.key}>
                    <TextField
                      label={field.label}
                      value={formValues[field.key]}
                      onChange={(event) => handleChange(field.key, event.target.value)}
                      error={Boolean(errors[field.key])}
                      helperText={errors[field.key] || field.helperText}
                      fullWidth
                      type="number"
                      disabled={!formValues[section.toggleKey]}
                      inputProps={{ step: field.step, min: field.min }}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        ))}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Tooltip title={t('fleetOptimisation.resultsTooltip')}>
            <Icon name="circle-info" size={20} />
          </Tooltip>
          <Typography variant="body2" color="text.secondary">
            {t('fleetOptimisation.resultsHint')}
          </Typography>
        </Box>

        <Button variant="contained" onClick={handleAnalyze} sx={{ mb: 3 }}>
          {t('fleetOptimisation.analyzeImpact')}
        </Button>

        {hasValidationError && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            {t('fleetOptimisation.validationWarning')}
          </Alert>
        )}

        <Typography variant="h6" sx={{ mb: 1 }}>
          {t('fleetOptimisation.financialOutcomes')}
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: '2px solid',
                borderColor: results.financialSavings >= 0 ? 'success.main' : 'error.main',
                backgroundColor: results.financialSavings >= 0 ? '#f0fdf4' : '#fef2f2',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {annualFinancialLabel}
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  color: results.financialSavings >= 0 ? 'success.dark' : 'error.dark',
                  fontWeight: 700,
                }}
              >
                {GBP_FORMATTER.format(results.financialSavings)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('fleetOptimisation.currentVsOptimizedAnnual', { current: GBP_FORMATTER.format(results.baselineCost), optimized: GBP_FORMATTER.format(results.optimizedCost) })}
              </Typography>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: '2px solid',
                borderColor: results.totalFinancialSavings >= 0 ? 'success.main' : 'error.main',
                backgroundColor: results.totalFinancialSavings >= 0 ? '#f0fdf4' : '#fef2f2',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {totalFinancialLabel}
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  color: results.totalFinancialSavings >= 0 ? 'success.dark' : 'error.dark',
                  fontWeight: 700,
                }}
              >
                {GBP_FORMATTER.format(results.totalFinancialSavings)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('fleetOptimisation.currentVsOptimizedTotal', { current: GBP_FORMATTER.format(results.totalBaselineCost), optimized: GBP_FORMATTER.format(results.totalOptimizedCost) })}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Typography variant="h6" sx={{ mb: 1 }}>
          {t('fleetOptimisation.co2Outcomes')}
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: '2px solid',
                borderColor: results.co2ReductionTonnes >= 0 ? 'success.main' : 'error.main',
                backgroundColor: results.co2ReductionTonnes >= 0 ? '#f0fdfa' : '#fef2f2',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {annualCo2Label}
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  color: results.co2ReductionTonnes >= 0 ? 'success.dark' : 'error.dark',
                  fontWeight: 700,
                }}
              >
                {t('fleetOptimisation.tonnesValue', { value: results.co2ReductionTonnes.toFixed(2) })}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('fleetOptimisation.percentageReduction', { value: results.co2ReductionPercentage === null ? t('fleetOptimisation.notAvailable') : `${PERCENT_FORMATTER.format(results.co2ReductionPercentage)}%` })}
              </Typography>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: '2px solid',
                borderColor: results.totalCo2ReductionTonnes >= 0 ? 'success.main' : 'error.main',
                backgroundColor: results.totalCo2ReductionTonnes >= 0 ? '#f0fdfa' : '#fef2f2',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {totalCo2Label}
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  color: results.totalCo2ReductionTonnes >= 0 ? 'success.dark' : 'error.dark',
                  fontWeight: 700,
                }}
              >
                {t('fleetOptimisation.tonnesValue', { value: results.totalCo2ReductionTonnes.toFixed(2) })}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('fleetOptimisation.percentageReduction', { value: results.totalCo2ReductionPercentage === null ? t('fleetOptimisation.notAvailable') : `${PERCENT_FORMATTER.format(results.totalCo2ReductionPercentage)}%` })}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            {t('fleetOptimisation.co2Composition')}
          </Typography>
          <Grid container spacing={1}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="body1">
                {t('fleetOptimisation.annualCurrentVsOptimized', { current: results.baselineCo2Tonnes.toFixed(2), optimized: results.optimizedCo2Tonnes.toFixed(2) })}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="body1">
                {t('fleetOptimisation.totalCurrentVsOptimized', { current: results.totalBaselineCo2Tonnes.toFixed(2), optimized: results.totalOptimizedCo2Tonnes.toFixed(2) })}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary">
            {t('fleetOptimisation.capexComposition')}
          </Typography>
          <Typography variant="body1">
            {t('fleetOptimisation.baselineCapexIncluded', { value: results.baselineCapexIncluded ? t('fleetOptimisation.yes') : t('fleetOptimisation.no') })}
          </Typography>
          <Typography variant="body1">
            {t('fleetOptimisation.baselineCapexAnnual', { value: GBP_FORMATTER.format(results.baselineCapexAnnualAddition) })}
          </Typography>
          <Typography variant="body1">
            {t('fleetOptimisation.optimizedCapexIncluded', { value: results.optimizedCapexIncluded ? t('fleetOptimisation.yes') : t('fleetOptimisation.no') })}
          </Typography>
          <Typography variant="body1">
            {t('fleetOptimisation.optimizedCapexAnnual', { value: GBP_FORMATTER.format(results.optimizedCapexAnnualAddition) })}
          </Typography>
        </Paper>

        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper variant="outlined" sx={{ p: 2, height: { xs: 260, md: 320 } }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                {t('fleetOptimisation.charts.costComparison')}
              </Typography>
              <Bar
                data={costChartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { display: true } },
                  scales: {
                    y: {
                      ticks: {
                        callback: (value) => GBP_FORMATTER.format(Number(value)),
                      },
                    },
                  },
                }}
              />
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper variant="outlined" sx={{ p: 2, height: { xs: 260, md: 320 } }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                {t('fleetOptimisation.charts.co2Comparison')}
              </Typography>
              <Bar
                data={co2ChartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { display: true } },
                  scales: {
                    y: {
                      ticks: {
                        callback: (value) => t('fleetOptimisation.charts.tonnesAxis', { value: Number(value).toFixed(2) }),
                      },
                    },
                  },
                }}
              />
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default FleetOptimisationPage;

